import {
  fleetVehicleDeleteBlockedDetailsSchema,
  type FleetOperationalStatus,
} from '@gb-ticket/shared-types';
import { useState } from 'react';
import { toast } from 'sonner';

import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { ApiRequestError } from '@/lib/api';
import { useDeleteFleetVehicle, useRetireFleetVehicle } from '@/products/fleet/features/use-fleet';

export function FleetVehicleDeleteButton({
  vehicle,
  onDeleted,
}: {
  vehicle: {
    id: string;
    licensePlate: string;
    make: string;
    model: string;
    operationalStatus: FleetOperationalStatus;
  };
  onDeleted?: () => void;
}) {
  const remove = useDeleteFleetVehicle(vehicle.id);
  const retire = useRetireFleetVehicle(vehicle.id);
  const [open, setOpen] = useState(false);
  const [blocked, setBlocked] = useState<string[] | null>(null);
  const [canRetire, setCanRetire] = useState(vehicle.operationalStatus !== 'retired');
  const label = `${vehicle.licensePlate} (${vehicle.make} ${vehicle.model})`;
  const pending = remove.isPending || retire.isPending;

  function reset() {
    setBlocked(null);
    setCanRetire(vehicle.operationalStatus !== 'retired');
  }

  async function confirmDelete() {
    try {
      await remove.mutateAsync();
      setOpen(false);
      toast.success(`${label} wurde gelöscht.`);
      onDeleted?.();
    } catch (error) {
      const details =
        error instanceof ApiRequestError
          ? fleetVehicleDeleteBlockedDetailsSchema.safeParse(error.error.details)
          : null;
      if (details?.success) {
        setBlocked(details.data.reasons);
        setCanRetire(details.data.canRetire);
        return;
      }
      toast.error(
        error instanceof ApiRequestError
          ? error.message
          : 'Das Fahrzeug konnte nicht gelöscht werden.',
      );
    }
  }

  async function confirmRetire() {
    try {
      await retire.mutateAsync();
      setOpen(false);
      toast.success(`${label} wurde stillgelegt. Die Historie bleibt lesbar.`);
    } catch (error) {
      toast.error(
        error instanceof ApiRequestError
          ? error.message
          : 'Das Fahrzeug konnte nicht stillgelegt werden.',
      );
    }
  }

  return (
    <>
      <Button
        type="button"
        variant="outline"
        className="min-h-11"
        onClick={() => {
          reset();
          setOpen(true);
        }}
      >
        Löschen
      </Button>
      <AlertDialog
        open={open}
        onOpenChange={(next) => {
          if (!next && !pending) {
            setOpen(false);
            reset();
          }
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {blocked ? `${label} bleibt gespeichert` : `${label} löschen?`}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {blocked
                ? canRetire
                  ? 'Die vorhandenen Daten werden nicht mitgelöscht. Das Fahrzeug kann stillgelegt werden. Es verschwindet aus Buchung und Erinnerung, die Historie bleibt lesbar.'
                  : 'Das Fahrzeug ist bereits stillgelegt. Die vorhandenen Daten bleiben gespeichert.'
                : 'Gelöscht wird nur dieses Fahrzeug, und nur wenn keine Zuordnung, Prüfung, Kilometer, Schäden, Dokumente, Werkstatt, Fahrten oder Tankdaten vorhanden sind.'}
            </AlertDialogDescription>
          </AlertDialogHeader>
          {blocked && blocked.length > 0 ? (
            <ul className="list-disc space-y-1 pl-5 text-sm">
              {blocked.map((reason) => (
                <li key={reason}>{reason}</li>
              ))}
            </ul>
          ) : null}
          <AlertDialogFooter>
            <AlertDialogCancel disabled={pending}>Abbrechen</AlertDialogCancel>
            {blocked && canRetire ? (
              <Button
                type="button"
                className="min-h-11"
                disabled={pending}
                onClick={() => void confirmRetire()}
              >
                Stilllegen
              </Button>
            ) : null}
            {blocked ? null : (
              <Button
                type="button"
                variant="destructive"
                className="min-h-11"
                disabled={pending}
                onClick={() => void confirmDelete()}
              >
                {remove.isPending ? 'Wird gelöscht …' : 'Löschen'}
              </Button>
            )}
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
